/**
 * What is on the other side, what we already hold, and the two buttons that
 * change that.
 *
 * The third column is the one worth the table. "Ours" counts the rows that did
 * NOT come from the old CRM, and it is here rather than on the undo block
 * because that is the number an operator needs BEFORE pressing anything: it is
 * the part of this CRM that the import cannot reach and the undo will not
 * delete, and reading it afterwards is reading it too late.
 *
 * The dry run is the loud button and the real import is the quiet one, which is
 * the opposite of how a form usually looks. Deliberate: the safe action is the
 * one somebody should press first, and the real import carries its weight in
 * the confirmation step rather than in its colour.
 */
import { useState } from "react";
import { Loader2, Play, ShieldCheck, TriangleAlert } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import type { EntityCoverage } from "../../../../lib/crmImport";
import { Btn, Card, CardHead, EmptyState, fmtNum } from "../../../ui";
import { entityLabel } from "./labels";

export function EntityCard({
  entities,
  selected,
  onToggle,
  onAll,
  onRun,
  busy,
  canRun,
}: {
  entities: EntityCoverage[];
  selected: ReadonlySet<string>;
  onToggle: (entity: string, on: boolean) => void;
  onAll: (on: boolean) => void;
  onRun: (dryRun: boolean) => void;
  /** A run is starting or already going: every control here stands down. */
  busy: boolean;
  /** False while the legacy database cannot be read at all. */
  canRun: boolean;
}) {
  const t = useT();
  const [confirming, setConfirming] = useState(false);

  // A table this installation does not have cannot be ticked: `table: null` is
  // the introspection saying the source table is absent, not that it is empty.
  const runnable = entities.filter((row) => row.table !== null);
  const allOn = runnable.length > 0 && runnable.every((row) => selected.has(row.entity));
  const chosen = runnable.filter((row) => selected.has(row.entity)).length;

  const totals = entities.reduce(
    (acc, row) => ({
      sourceRows: acc.sourceRows + row.sourceRows,
      importedRows: acc.importedRows + row.importedRows,
      ownRows: acc.ownRows + row.ownRows,
    }),
    { sourceRows: 0, importedRows: 0, ownRows: 0 },
  );

  function start(dryRun: boolean) {
    setConfirming(false);
    onRun(dryRun);
  }

  return (
    <Card>
      <CardHead title={t("crmImport.entities.title")} sub={t("crmImport.entities.desc")} />

      <div className="px-6 pb-6 pt-1">
        {entities.length === 0 ? (
          <EmptyState
            icon={TriangleAlert}
            title={t("crmImport.entities.emptyTitle")}
            body={t("crmImport.entities.empty")}
          />
        ) : (
          <>
            <div className="crm-cov-scroll">
              <table className="crm-cov-tbl">
                <thead>
                  <tr>
                    <th scope="col">
                      <label className="flex cursor-pointer items-center gap-2.5">
                        <input
                          type="checkbox"
                          className="size-4 flex-none accent-[var(--color-primary)]"
                          checked={allOn}
                          disabled={busy || runnable.length === 0}
                          onChange={(e) => onAll(e.target.checked)}
                          aria-label={t("crmImport.entities.selectAll")}
                        />
                        {t("crmImport.entities.entity")}
                      </label>
                    </th>
                    <th scope="col">{t("crmImport.entities.sourceRows")}</th>
                    <th scope="col">{t("crmImport.entities.importedRows")}</th>
                    <th scope="col">{t("crmImport.entities.ownRows")}</th>
                  </tr>
                </thead>
                <tbody>
                  {entities.map((row) => {
                    const absent = row.table === null;
                    return (
                      <tr key={row.entity}>
                        <th scope="row">
                          <label className="flex cursor-pointer items-center gap-2.5">
                            <input
                              type="checkbox"
                              className="size-4 flex-none accent-[var(--color-primary)]"
                              checked={selected.has(row.entity)}
                              disabled={busy || absent}
                              onChange={(e) => onToggle(row.entity, e.target.checked)}
                            />
                            <span className="min-w-0">
                              {entityLabel(t, row.entity)}
                              <span className="block text-[11px] font-normal opacity-60">
                                {/* The resolved table name as the server found
                                    it, prefix included - it is what somebody
                                    checking the source would type. */}
                                {absent ? t("crmImport.entities.missingTable") : row.table}
                              </span>
                            </span>
                          </label>
                        </th>
                        <td>{fmtNum(row.sourceRows)}</td>
                        <td>{fmtNum(row.importedRows)}</td>
                        <td>{fmtNum(row.ownRows)}</td>
                      </tr>
                    );
                  })}
                </tbody>
                {/* `crm-cov-tbl` styles tbody and thead only, so the totals row
                    carries its own rule and alignment rather than inheriting
                    the centred, unruled default and reading as one more entity. */}
                <tfoot className="border-t border-[var(--crm-hair)] font-bold">
                  <tr>
                    <th scope="row" className="!text-left">
                      {t("crmImport.entities.total")}
                    </th>
                    <td>{fmtNum(totals.sourceRows)}</td>
                    <td>{fmtNum(totals.importedRows)}</td>
                    <td>{fmtNum(totals.ownRows)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <p className="crm-sub mt-3 text-[11.5px] leading-snug">{t("crmImport.entities.ownRowsHint")}</p>

            {confirming ? (
              <div className="crm-note mt-4">
                <div className="crm-note-t">
                  <TriangleAlert className="size-4" />
                  {t("crmImport.actions.confirmTitle")}
                </div>
                <p className="crm-note-p">{t("crmImport.actions.confirmBody")}</p>
                <div className="mt-3 flex flex-wrap justify-end gap-2">
                  <Btn onClick={() => setConfirming(false)}>{t("crmImport.actions.cancel")}</Btn>
                  <Btn tone="dark" onClick={() => start(false)} disabled={busy}>
                    {busy ? <Loader2 className="size-4 animate-spin" /> : <Play className="size-4" />}
                    {t("crmImport.actions.confirmYes")}
                  </Btn>
                </div>
              </div>
            ) : (
              <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
                <Btn onClick={() => setConfirming(true)} disabled={busy || !canRun || chosen === 0}>
                  <Play className="size-4" />
                  {t("crmImport.actions.run")}
                </Btn>
                <Btn tone="dark" onClick={() => start(true)} disabled={busy || !canRun || chosen === 0}>
                  {busy ? <Loader2 className="size-4 animate-spin" /> : <ShieldCheck className="size-4" />}
                  {t("crmImport.actions.dryRun")}
                </Btn>
              </div>
            )}

            <p className="crm-sub mt-2 text-right text-[11.5px] leading-snug">
              {t(canRun ? "crmImport.actions.dryRunHint" : "crmImport.actions.blocked")}
            </p>
          </>
        )}
      </div>
    </Card>
  );
}
